import type { Template } from "@pdfme/common";

/**
 * Shows the print bleed in the editor: the page is drawn larger by the bleed on every side, the
 * bleed area is tinted red and the label edge (trim line) is outlined; both are drawn as static
 * elements that can't be selected. Any element can be moved into the bleed (it may be cut off
 * there). Elements are shifted by the bleed while editing and shifted back when the template is
 * read (save, generate), so the stored template never contains this: no padding, positions
 * relative to the label (elements in the bleed have negative positions or go past the size).
 *
 * `scale` = template units per mm (1; about 2.83 for old templates stored in points).
 */
type Pos = { x: number; y: number };
type Base = { width?: unknown; height?: unknown; padding?: unknown; staticSchema?: unknown };

const round = (v: number) => Math.round(v * 10000) / 10000;
const MARK = "__bleed_";

/** Red bands over the bleed and a thin line on the label edge (editor only). */
function bleedMarks(w: number, h: number, b: number): unknown[] {
  const band = (n: string, x: number, y: number, bw: number, bh: number) => ({
    name: `${MARK}${n}`, type: "rectangle", position: { x, y }, width: bw, height: bh,
    color: "#ff0000", opacity: 0.18, borderWidth: 0, borderColor: "#ff0000", readOnly: true,
  });
  const W = w + 2 * b;
  return [
    band("top", 0, 0, W, b),
    band("bottom", 0, h + b, W, b),
    band("left", 0, b, b, h),
    band("right", w + b, b, b, h),
    { name: `${MARK}trim`, type: "rectangle", position: { x: b, y: b }, width: w, height: h, color: "", borderWidth: 0.15, borderColor: "#ff0000", readOnly: true },
  ];
}

const withoutMarks = (list: unknown) =>
  Array.isArray(list) ? list.filter((s) => !String((s as { name?: unknown }).name ?? "").startsWith(MARK)) : list;

function shift<T>(list: unknown, d: number): T {
  if (!Array.isArray(list)) return list as T;
  return list.map((s) => {
    const p = (s as { position?: Pos }).position;
    return p ? { ...(s as object), position: { x: round(p.x + d), y: round(p.y + d) } } : s;
  }) as T;
}

function blank(template: Template): Base | null {
  const b = template.basePdf as Base;
  return b && typeof b === "object" && typeof b.width === "number" && typeof b.height === "number" ? b : null;
}

export function bleedInTemplateUnits(template: Template, bleedMm: number, labelWidthMm: number): number {
  const b = blank(template);
  if (!b || !(bleedMm > 0) || !(labelWidthMm > 0)) return 0;
  return round(bleedMm * ((b.width as number) / labelWidthMm));
}

/** Template as shown in the editor: page + bleed, bleed as red padding, elements shifted. */
export function toEditorView(template: Template, bleed: number): Template {
  const b = blank(template);
  if (!b) return template;
  const w = b.width as number;
  const h = b.height as number;
  return {
    ...template,
    basePdf: {
      ...b,
      width: round(w + 2 * bleed),
      height: round(h + 2 * bleed),
      padding: [0, 0, 0, 0],
      ...(bleed > 0 || b.staticSchema
        ? { staticSchema: [...((shift(b.staticSchema, bleed) as unknown[]) ?? []), ...(bleed > 0 ? bleedMarks(w, h, bleed) : [])] }
        : {}),
    } as Template["basePdf"],
    schemas: (template.schemas as unknown[]).map((page) => shift(page, bleed)) as Template["schemas"],
  };
}

/** Back to the stored form: label size, no padding, positions relative to the label. */
export function fromEditorView(template: Template, bleed: number): Template {
  const b = blank(template);
  if (!b) return template;
  const w = b.width as number;
  const h = b.height as number;
  return {
    ...template,
    basePdf: {
      ...b,
      width: round(w - 2 * bleed),
      height: round(h - 2 * bleed),
      padding: [0, 0, 0, 0],
      staticSchema: undefined,
      ...(b.staticSchema && (withoutMarks(b.staticSchema) as unknown[]).length ? { staticSchema: shift(withoutMarks(b.staticSchema), -bleed) } : {}),
    } as Template["basePdf"],
    schemas: (template.schemas as unknown[]).map((page) => shift(page, -bleed)) as Template["schemas"],
  };
}
